import React, {
  type ReactElement,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import cx from "classnames";
import { RegisterOptions } from "react-hook-form";
import purify from "dompurify";
import * as s from "../../shared/inputStyles";

type InputType = "text" | "password" | "email" | "number" | "date" | "tel";

export interface Props {
  disabled?: boolean;
  variant?: "dark" | "light";
  hasError?: boolean;
  errorMessage?: string;
  helperText?: React.ReactNode;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  id: string;
  label?: string;
  labelInfoIcon?: React.ReactElement;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  register: any;
  type?: InputType;
  defaultValue?: string;
  value?: string;
  validationOptions?: Partial<RegisterOptions>;
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export default function Input({
  disabled = false,
  variant = "light",
  hasError = false,
  errorMessage,
  helperText,
  iconLeft,
  iconRight,
  id,
  label,
  placeholder,
  readOnly = false,
  required = false,
  register,
  type = "text",
  defaultValue,
  value,
  validationOptions,
  labelInfoIcon,
  onInputKeyDown,
  onChange,
  ...props
}: Props): ReactElement {
  const ariaProps: Partial<InputHTMLAttributes<HTMLInputElement>> = {
    "aria-label": label,
    "aria-invalid": hasError ? "true" : "false",
    ...(hasError ? { "aria-describedby": "helperText" } : {}),
  };

  const isDark = variant === "dark";

  return (
    <div className={s.inputGroup}>
      {label && (
        <label
          className={cx(s.label, {
            [s.labelDisabled]: disabled,
            [s.labelDark]: isDark && !disabled,
          })}
          htmlFor={id}
        >
          {label}
          {required && (
            <span
              className={cx(s.labelRequired, {
                [s.labelRequiredDark]: isDark,
              })}
            >
              *
            </span>
          )}
          {labelInfoIcon && <span className="ml-0.5">{labelInfoIcon}</span>}
        </label>
      )}
      <div className="relative">
        <input
          disabled={disabled}
          id={id}
          className={cx(isDark ? s.inputDark : s.input, {
            "pl-12": Boolean(iconLeft),
            "pr-12": Boolean(iconRight),
            [s.inputError]: hasError,
            [s.inputDisabled]: disabled || readOnly,
          })}
          data-variant={isDark ? "dark" : "light"}
          placeholder={placeholder}
          readOnly={readOnly}
          defaultValue={defaultValue}
          value={value}
          type={type}
          onKeyDown={onInputKeyDown}
          {...register(id, {
            ...validationOptions,
            setValueAs: (value: string) => purify.sanitize(value),
            onChange,
          })}
          {...ariaProps}
          {...props}
        />
        {iconLeft && (
          <span
            className={cx(s.icon, "left-4", {
              [s.iconError]: hasError,
              [s.iconDisabled]: disabled,
            })}
          >
            {iconLeft}
          </span>
        )}
        {iconRight && (
          <span
            className={cx(s.icon, "right-4", {
              [s.iconError]: hasError,
              [s.iconDisabled]: disabled,
            })}
          >
            {iconRight}
          </span>
        )}
      </div>
      {helperText && (
        <span
          id="helperText"
          className={cx(s.helperText, {
            [s.helperTextDisabled]: disabled,
            [s.helperTextDark]: isDark,
          })}
        >
          {helperText}
        </span>
      )}
      {hasError && errorMessage && (
        <span
          className={cx(s.errorText, {
            [s.errorTextDark]: isDark,
          })}
        >
          {errorMessage}
        </span>
      )}
    </div>
  );
}
